import type { AccountCancellationPolicyResponseDto } from '@shared/core/cancellation-policy'
import type { AppointmentTypeDto } from '@shared/core/appointment-types'
import { useSnackbar } from 'notistack'
import { ForwardedRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import type { OtherSettingsCancellationPolicyFormHandle } from 'src/modules/user/components/settings-profile-info/other-settings-form-handles'
import { useAppointmentTypes } from 'src/modules/user/hooks/use-appointment-types'
import {
  useCancellationPolicy,
  useLateCancelCounts,
  useResetCancellationPolicyTypes,
  useUpdateCancellationPolicy,
} from '../../hooks'
import {
  changedPolicyFields,
  describeCustomPolicy,
  toAccountPolicyDto,
  toPolicyFormValues,
  validatePolicyForm,
} from '../../utils'
import type { CancellationPolicyFormErrors } from '../../types/cancellation-policy-form.types'
import type { PolicyFieldRefs } from '../cancellation-policy-fields'
import type { ResetAffectedType } from '../cancellation-policy-reset-modal'
import type { CancellationPolicySettingsSectionProps } from './cancellation-policy-settings-section.interface'
import { usePolicyForm } from './use-policy-form'

function modeOf(type: AppointmentTypeDto) {
  return type.cancellation_policy_mode ?? 'account'
}

function toResetAffectedTypes(
  types: AppointmentTypeDto[],
  policy: AccountCancellationPolicyResponseDto | undefined,
): ResetAffectedType[] {
  return types
    .filter((type) => modeOf(type) !== 'account')
    .map((type) => ({
      id: type.id,
      name: type.name,
      summary: modeOf(type) === 'none' ? 'No policy' : describeCustomPolicy(policy?.custom_policies?.[type.id]),
    }))
}

// The page Save sits far below this section, so an error shown here would otherwise go unseen.
function revealFirstError(errors: CancellationPolicyFormErrors, fieldRefs: PolicyFieldRefs) {
  const field = fieldRefs[errors.notice_value ? 'notice_value' : 'policy_text'].current
  if (!field) return
  field.scrollIntoView({ block: 'center', inline: 'nearest' })
  field.focus({ preventScroll: true })
}

function usePolicyFieldRefs(): PolicyFieldRefs {
  const noticeValue = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  const policyText = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  return useMemo(() => ({ notice_value: noticeValue, policy_text: policyText }), [])
}

function useResetTypesAction(affectedCount: number) {
  const { enqueueSnackbar } = useSnackbar()
  const resetTypes = useResetCancellationPolicyTypes()
  const [isResetOpen, setIsResetOpen] = useState(false)

  const confirmReset = useCallback(async () => {
    try {
      const { count } = await resetTypes.mutateAsync()
      enqueueSnackbar(`${count} appointment ${count === 1 ? 'type now uses' : 'types now use'} the account policy.`, {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar('Could not reset the appointment types. Please try again.', { variant: 'error' })
    } finally {
      setIsResetOpen(false)
    }
  }, [enqueueSnackbar, resetTypes])

  return {
    isResetOpen,
    openReset: () => affectedCount > 0 && setIsResetOpen(true),
    closeReset: () => setIsResetOpen(false),
    confirmReset,
    isResetting: resetTypes.isLoading,
  }
}

export function useCancellationPolicySettingsSection(
  { userId, onDirtyChange }: Pick<CancellationPolicySettingsSectionProps, 'userId' | 'onDirtyChange'>,
  ref: ForwardedRef<OtherSettingsCancellationPolicyFormHandle>,
) {
  const policyQuery = useCancellationPolicy()
  const { data: types = [] } = useAppointmentTypes(userId, { enabled: !!userId })
  const { data: counts } = useLateCancelCounts()
  const updatePolicy = useUpdateCancellationPolicy()
  const saved = policyQuery.data
  const policyForm = usePolicyForm(saved)
  const { form, setErrors, turnOnConfirmedRef } = policyForm
  const isDirty = form.formState.isDirty
  const fieldRefs = usePolicyFieldRefs()

  const affectedTypes = useMemo(() => toResetAffectedTypes(types, saved), [types, saved])
  const typesUsingAccount = types.length - affectedTypes.length

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const validate = useCallback(() => {
    const errors = validatePolicyForm(form.getValues())
    setErrors(errors)
    const isValid = !errors.notice_value && !errors.policy_text
    if (!isValid) revealFirstError(errors, fieldRefs)
    return isValid
  }, [fieldRefs, form, setErrors])

  const needsConfirmation = useCallback(() => {
    const values = form.getValues()
    if (!saved || !values.enabled) return false
    // The turn-on modal already warned that the policy applies right away, whatever else changes with it.
    if (!saved.enabled && turnOnConfirmedRef.current) return false
    return changedPolicyFields(values, saved).length > 0
  }, [form, saved, turnOnConfirmedRef])

  const submitIfDirty = useCallback(async () => {
    if (!saved || !form.formState.isDirty || !validate()) return false
    try {
      const updated = await updatePolicy.mutateAsync(toAccountPolicyDto(form.getValues()))
      form.reset(toPolicyFormValues(updated))
      turnOnConfirmedRef.current = false
      return true
    } catch {
      return false
    }
  }, [form, saved, turnOnConfirmedRef, updatePolicy, validate])

  useImperativeHandle(ref, () => ({ submitIfDirty, validate, needsConfirmation }), [
    needsConfirmation,
    submitIfDirty,
    validate,
  ])

  return {
    policyQuery,
    policyForm,
    fieldRefs,
    affectedTypes,
    typesUsingAccount,
    totalTypes: types.length,
    counts,
    ...useResetTypesAction(affectedTypes.length),
  }
}
